import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';

import React, { useEffect, useState } from 'react';
import { redirect } from 'redux-first-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import t from '../i18n/intl';
import config from '../../appconfig';
import MACPageHeader from './components/macPageHeader';
import { Dimensions } from '../../components/visuals';
import { State } from '../../model/State';
import { messages } from './messages';
import * as marketplace from 'modules/marketplace';
import AddonNotFoundBanner from 'modules/addon/AddonNotFoundBanner';

import { selectBTFProducts, selectProductPackage } from './duck/selectors/products';
import { fetchCountryByName } from './duck/actions/country';
import {
  getCurrentMACContactEmail,
  getCurrentMACContactOrganization,
  getCurrentMACContactOrgCountry,
  selectCountryByName,
  selectCreatedPersistedLicense,
  selectIsEvaluationAllowed,
} from './duck/selectors';
import { route } from './duck/actions/landing';
import { fetchProductPackage } from './duck/actions/product';
import { fetchIsEvaluationAllowed } from './duck/actions/accounts';
import { createPersistedLicenseAction } from './duck/actions/accountId';
import { addonLicenseView } from './duck/actions';
import { selectVendorDetails } from './duck/selectors/marketplace';
import {
  ContentBody,
  DetailLineWrapper,
  DetailsValueLabelWrapper,
  PageH3Header,
  SpinnerWrapper,
  StyledLoadingButton,
} from './components/styledComponents';

const TextfieldWrapper = styled.div`
  width: ${Dimensions.units(37.5)}px;
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonsWrapper = styled.div`
  margin-left: ${Dimensions.units(15)}px;
  display: flex;
`;

const DetailLine = ({ labelText, dataCy = '', children }) => {
  return (
    <DetailLineWrapper {...(dataCy ? { 'data-cy': dataCy } : {})}>
      <DetailsValueLabelWrapper>
        {labelText && <FormattedMessage {...labelText} />}
      </DetailsValueLabelWrapper>
      {children}
    </DetailLineWrapper>
  );
};

const LoadingState = () => (
  <SpinnerWrapper $marginTop={20} $height={20}>
    <Spinner size="large" />
  </SpinnerWrapper>
);

enum ErrorTypes {
  INVALID_KEY = 'INVALID_KEY',
  RESTRICTED_COUNTRY = 'RESTRICTED_COUNTRY',
  EOL_PRODUCT = 'EOL_PRODUCT',
  MAX_EVAL_ERROR = 'MAX_EVAL_ERROR',
  NONE = 'NONE',
}

const ErrorMap = {
  [ErrorTypes.INVALID_KEY]: {
    title: t(messages.invalidAddonKeyTitle),
    message: messages.invalidAddonKeyDescription,
    values: {
      contactUsLink: <a href={config.companyContact}>{t('common.contact-us')}</a>,
    },
  },
  [ErrorTypes.RESTRICTED_COUNTRY]: {
    title: t('mac.license.addon.evaluation.restricted.country.title'),
    message: messages.restrictedCountryDescription,
    values: {
      learnMoreLink: (
        <a href="https://www.atlassian.com/licensing/marketplace#frequentlyaskedquestions-12">
          <FormattedMessage {...messages.restrictedCountryLearnMore} />
        </a>
      ),
    },
  },
  [ErrorTypes.EOL_PRODUCT]: {
    title: t('mac.license.addon.evaluation.eol.server.title'),
    message: messages.addonEolError,
    values: {
      exploreCloudUrl: (
        <a href="https://www.atlassian.com/migration/assess/journey-to-cloud">
          <FormattedMessage {...messages.addonEolExploreCloud} />
        </a>
      ),
    },
  },
  [ErrorTypes.MAX_EVAL_ERROR]: {
    title: t('mac.license.addon.evaluation.max.eval.title'),
    message: messages.addonMaxEvalError,
  },
};

const ErrorSection = ({ errorType, additionalValues }) => {
  if (!errorType || errorType === ErrorTypes.NONE) return null;
  const error = ErrorMap[errorType];

  return (
    <SectionMessage appearance="error" title={error.title}>
      <FormattedMessage {...error.message} values={{ ...error.values, ...additionalValues }} />
    </SectionMessage>
  );
};

const addonMarketplaceTermsLink = (chunks) => (
  <a
    href="https://www.atlassian.com/licensing/marketplace/termsofuse"
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </a>
);

const vendorTermsLink = (chunks, vendorDetails) => (
  <a href={vendorDetails?.termsLink || ''} target="_blank" rel="noreferrer">
    {chunks}
  </a>
);

const AddonLicenseEvaluationPage = () => {
  const [errorType, setErrorType] = useState<ErrorTypes>(ErrorTypes.NONE);
  const [isFeCruApp, setIsFeCruApp] = useState(false);

  const contactOrganization = useSelector(getCurrentMACContactOrganization);
  const countryName = useSelector(getCurrentMACContactOrgCountry);
  const userEmail = useSelector(getCurrentMACContactEmail);
  const isEvaluationAllowed = useSelector(selectIsEvaluationAllowed);
  const addonKey = useSelector((state) => (state as State).location.payload.addonKey);
  const btfProductsProgress = useSelector((state) => selectBTFProducts(state as State, addonKey));
  const productPackageDetails = btfProductsProgress?.ifSuccess((r) => r)?.details;
  const [organizationName, setOrganizationName] = useState(contactOrganization);
  const [isCheckedMarketplaceTerms, setIsCheckedMarketplaceTerms] = useState(false);
  const [isCheckedVendorTerms, setIsCheckedVendorTerms] = useState(false);
  const vendorDetails = useSelector((state) => selectVendorDetails(state as State, addonKey));
  const countryDetails = useSelector((state) =>
    selectCountryByName(state as State, countryName || ''),
  );
  const parentProductPackage = useSelector((state) =>
    selectProductPackage(state as State, productPackageDetails?.parentId),
  );
  const createLicenseResponse = useSelector(selectCreatedPersistedLicense);

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      addonKey.includes('crucible') ||
      addonKey.includes('fisheye') ||
      addonKey.includes('fecru')
    ) {
      if (addonKey !== 'com.mohamicorp.bitbucket.plugin.crucible-review-hook.data-center') {
        setIsFeCruApp(true);
      }
    }
  }, [addonKey]);

  useEffect(() => {
    if (countryName) {
      dispatch(fetchCountryByName(countryName));
    }
  }, [countryName]);

  useEffect(() => {
    if (contactOrganization) {
      setOrganizationName(contactOrganization);
    }
  }, [contactOrganization]);

  useEffect(() => {
    if (isEvaluationAllowed === false) {
      setErrorType(ErrorTypes.MAX_EVAL_ERROR);
    }
  }, [isEvaluationAllowed]);

  useEffect(() => {
    if (productPackageDetails && countryDetails) {
      if (productPackageDetails.marketplaceAddOn && !countryDetails.marketplaceEnabled) {
        setErrorType(ErrorTypes.RESTRICTED_COUNTRY);
      }
    }
  }, [btfProductsProgress, countryDetails]);

  useEffect(() => {
    if (userEmail && productPackageDetails) {
      dispatch(fetchIsEvaluationAllowed(userEmail, productPackageDetails.key));
    }
  }, [btfProductsProgress, userEmail]);

  useEffect(() => {
    if (btfProductsProgress.isCompleted && !productPackageDetails) {
      setErrorType(ErrorTypes.INVALID_KEY);
    }
    if (productPackageDetails) {
      dispatch(fetchProductPackage(productPackageDetails.parentId));
    }
  }, [btfProductsProgress]);

  useEffect(() => {
    if (parentProductPackage) {
      const isNotAllowedServerProduct = ![
        'crucible',
        'pricingplan.crucible',
        'fisheye',
        'pricingplan.fisheye',
      ].includes(parentProductPackage?.details?.key);

      const isDataCenter = parentProductPackage?.details?.isDataCenter;

      if (!isDataCenter && isNotAllowedServerProduct) {
        setErrorType(ErrorTypes.EOL_PRODUCT);
      }
    }
  }, [parentProductPackage]);

  useEffect(() => {
    if (createLicenseResponse?.success) {
      dispatch(
        redirect(
          addonLicenseView.route(createLicenseResponse._result.licenseDetails.id.toString(), {}),
        ),
      );
    } else if (createLicenseResponse?.error) {
      setErrorType(ErrorTypes.INVALID_KEY);
    }
  }, [createLicenseResponse]);

  const generateLicenseClick = () => {
    dispatch(
      createPersistedLicenseAction(
        productPackageDetails.key,
        organizationName?.trim() || contactOrganization || '',
        null,
      ),
    );
  };

  return (
    <>
      <MACPageHeader />
      <PageH3Header>
        <FormattedMessage {...messages.licenseEvaluationPageTitle} />
      </PageH3Header>

      <ErrorSection
        errorType={errorType}
        additionalValues={{ addOnName: productPackageDetails?.description, country: countryName }}
      />

      {productPackageDetails && parentProductPackage ? (
        <>
          <hr />
          {isFeCruApp ? (
            <AddonNotFoundBanner
              events={{ onSearchForAddonsClick: () => dispatch(marketplace.actions.route()) }}
            />
          ) : (
            <ContentBody $marginTop={5}>
              <DetailLine labelText={messages.productLabel}>
                {productPackageDetails?.description}
              </DetailLine>

              <DetailLine labelText={messages.organization}>
                <TextfieldWrapper>
                  <Textfield
                    data-cy="organization-name"
                    value={organizationName || ''}
                    isRequired
                    onChange={(e) => setOrganizationName(e.currentTarget.value)}
                    maxLength={100}
                  />
                </TextfieldWrapper>
              </DetailLine>

              <DetailLine labelText="">
                <CheckBoxWrapper>
                  <Checkbox
                    data-cy="marketplace-terms-check"
                    isChecked={isCheckedMarketplaceTerms}
                    onChange={() => setIsCheckedMarketplaceTerms(!isCheckedMarketplaceTerms)}
                    label={
                      t(messages.addonMarketplaceTerms, {
                        termsOfUseLink: addonMarketplaceTermsLink,
                      }) as string
                    }
                  />
                  <Checkbox
                    data-cy="vendor-terms-check"
                    isChecked={isCheckedVendorTerms}
                    onChange={() => setIsCheckedVendorTerms(!isCheckedVendorTerms)}
                    label={
                      t(messages.addonVendorTerms, {
                        termsOfUseLink: (chunks) => vendorTermsLink(chunks, vendorDetails),
                        vendorName: vendorDetails?.vendorName || 'Vendor',
                      }) as string
                    }
                  />
                </CheckBoxWrapper>
              </DetailLine>

              <ButtonsWrapper>
                <StyledLoadingButton
                  appearance="primary"
                  onClick={() => generateLicenseClick()}
                  isDisabled={
                    !isCheckedMarketplaceTerms ||
                    !isCheckedVendorTerms ||
                    errorType !== ErrorTypes.NONE ||
                    isEvaluationAllowed !== true ||
                    !organizationName
                  }
                  isLoading={createLicenseResponse?.inProgress}
                  data-cy="btn-generate-license"
                >
                  <FormattedMessage {...messages.generateLicenseButtonMessage} />
                </StyledLoadingButton>
                <Button
                  onClick={() => {
                    dispatch(redirect(route()));
                  }}
                  appearance="subtle"
                >
                  <FormattedMessage {...messages.addNewContactModalCancel} />
                </Button>
              </ButtonsWrapper>
            </ContentBody>
          )}
        </>
      ) : (
        errorType === ErrorTypes.NONE && <LoadingState />
      )}
    </>
  );
};

export const MACAddonLicenseEvaluationPage = connect(null, null)(AddonLicenseEvaluationPage);
